import { useLocation } from 'react-router-dom';
import {
  canPromoteWithLegacyDeposit,
  getDepositGuaranteePromoTab,
  hasReachedDepositPromoDay,
} from './depositGuaranteePromoEligibility';
import { useFeature } from '@/apiHooks/featureSwitches.hook';
import { Feature } from '@/@types/featureSwitch';
import { useGetEntitlements } from '@/apiHooks/entitlements.hook';
import { useProducts } from '@/customHooks/useProducts';
import { ProductType } from '@/@types/product';
import { useGetSuchenPlusStatus } from '@/apiHooks/suchenPlusStatus.hook';
import { useGetRentDepositInsurance } from '@/apiHooks/rentDepositInsurance.hook';
import { useGetProductsStatus } from '@/apiHooks/productsStatus.hook';
import { isFeatureFlagEnabled } from '@/utils/isFeatureFlagEnabled.util';

export const useDepositGuaranteePromo = () => {
  const { pathname } = useLocation();
  const tab = getDepositGuaranteePromoTab(pathname);
  const enabled =
    useFeature(Feature.HOW_4690_MEINPLUS_DEPOSIT_GUARANTEE_PROMO) ||
    isFeatureFlagEnabled(Feature.HOW_4690_MEINPLUS_DEPOSIT_GUARANTEE_PROMO);
  const { entitlements, initializing: entitlementsInitializing } = useGetEntitlements();
  const { products, initializing: productsInitializing } = useProducts();

  const isCustomerDataReady = !productsInitializing && !entitlementsInitializing;
  const canCheckEligibility = enabled && tab !== null && isCustomerDataReady;
  const candidate =
    canCheckEligibility &&
    (products?.some(({ productType }) => productType === ProductType.MieterPlus) ?? false) &&
    hasReachedDepositPromoDay(entitlements);

  const { suchenPlusStatus, isError: suchenPlusError } = useGetSuchenPlusStatus({
    enabled: candidate,
    useErrorBoundary: false,
  });
  // Legacy buyer bundles can also expand into DOCUMENTS_PLUS_RENT. Require the rental Suchen+ status too.
  const rentalLevel = suchenPlusStatus?.mieter?.level;
  const checkOwnership = candidate && !suchenPlusError && !!rentalLevel;
  const { policy, isError: policyError } = useGetRentDepositInsurance(checkOwnership, undefined, {
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });
  const { productsStatus, isProductsStatusError: legacyError } = useGetProductsStatus(checkOwnership);

  const hasNoModernPolicy = !policyError && policy === null;
  const hasEligibleLegacyState =
    !legacyError && !!productsStatus && canPromoteWithLegacyDeposit(productsStatus.deposit);

  const isVisible = checkOwnership && hasNoModernPolicy && hasEligibleLegacyState;

  return isVisible ? tab : null;
};
